import type { ComponentProps } from 'react'
import { cn } from '@/lib/utils'

import dashboardIcon from '@/assets/sidebar/icon-dashboard.svg?raw'
import projectsIcon from '@/assets/sidebar/icon-projects.svg?raw'
import inquiriesIcon from '@/assets/sidebar/icon-inquiries.svg?raw'
import aiIcon from '@/assets/sidebar/icon-ai.svg?raw'
import newProjectIcon from '@/assets/sidebar/icon-new-project.svg?raw'
import plusIcon from '@/assets/sidebar/icon-plus.svg?raw'
import notificationIcon from '@/assets/sidebar/icon-notification.svg?raw'
import menuIcon from '@/assets/sidebar/icon-menu.svg?raw'
import settingsIcon from '@/assets/sidebar/icon-settings.svg?raw'
import trashIcon from '@/assets/sidebar/icon-trash.svg?raw'
import eyeIcon from '@/assets/sidebar/icon-eye.svg?raw'
import expertsIcon from '@/assets/sidebar/icon-experts.svg?raw'
import chevronIcon from '@/assets/sidebar/icon-chevron.svg?raw'
import walletIcon from '@/assets/sidebar/icon-wallet.svg?raw'
import monomoAiMark from '@/assets/dashboard/monomo-ai-mark.svg?raw'
import gridBg from '@/assets/sidebar/grid-bg.png'
import fourSquaresIcon from '@/assets/dashboard/four-squares-dots.svg?raw'

const monomoAiMarkIcon = monomoAiMark.replaceAll('#16A0F8', 'currentColor')

export const sidebarGridBgUrl = gridBg

type SidebarIconProps = {
  svg: string
  className?: string
}

/** Inlines a Figma SVG so `currentColor` fills inherit from the parent. */
export function SidebarIcon({ svg, className }: SidebarIconProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-5 shrink-0 items-center justify-center text-current [&_svg]:size-full',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

function createSidebarIcon(svg: string) {
  return function AssetIcon({ className }: { className?: string }) {
    return <SidebarIcon svg={svg} className={className} />
  }
}

export const SidebarDashboardIcon = createSidebarIcon(dashboardIcon)
export const SidebarProjectsIcon = createSidebarIcon(fourSquaresIcon)
export const SidebarInquiriesIcon = createSidebarIcon(inquiriesIcon)
export const SidebarAiIcon = createSidebarIcon(aiIcon)
export const SidebarMonomoAiMarkIcon = createSidebarIcon(monomoAiMarkIcon)
export const SidebarNewProjectIcon = createSidebarIcon(plusIcon)
export const SidebarPlusIcon = createSidebarIcon(plusIcon)
export const SidebarNotificationIcon = createSidebarIcon(notificationIcon)
export const SidebarMenuIcon = createSidebarIcon(menuIcon)
export const SidebarSettingsIcon = createSidebarIcon(settingsIcon)
export const SidebarTrashIcon = createSidebarIcon(trashIcon)
export const SidebarEyeIcon = createSidebarIcon(eyeIcon)
export const SidebarExpertsIcon = createSidebarIcon(expertsIcon)
export const SidebarWalletIcon = createSidebarIcon(walletIcon)
/** Trailing nav chevron - smaller than nav icons; must not use SidebarIcon (size-5 default). */
export function SidebarChevronIcon({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-2.5 shrink-0 items-center justify-center text-current',
        '[&_svg]:block [&_svg]:size-full',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: chevronIcon }}
    />
  )
}

export type SidebarNavIcon = (
  props: Pick<ComponentProps<'span'>, 'className'>,
) => React.JSX.Element
